import { useState } from "react";
import { useNavigate } from "react-router";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DatePicker } from "@mui/x-date-pickers";
import { formatISO } from "date-fns";
import {
  useCreateBenchmarkReportMutation,
  useDeleteBenchmarkReportMutation,
  useGetBenchmarkReportsQuery,
} from "../services/api";

const formatDate = (d) =>
  new Date(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const BenchmarkPage = () => {
  const [name, setName] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const navigate = useNavigate();

  const { data: reports, isLoading: reportsLoading } =
    useGetBenchmarkReportsQuery();

  const [createReport, { isLoading: creatingReport }] =
    useCreateBenchmarkReportMutation();

  const [deleteReport] = useDeleteBenchmarkReportMutation();

  const handleCreateReport = async () => {
    if (!name || !dateFrom || !dateTo) return;
    const result = await createReport({
      name,
      dateFrom: formatISO(dateFrom, { representation: "date" }),
      dateTo: formatISO(dateTo, { representation: "date" }),
    });
    if (result.data?._id) {
      navigate(`/benchmark/${result.data._id}`);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Benchmark
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Create Report
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          />
          <DatePicker
            label="Date From"
            value={dateFrom}
            onChange={setDateFrom}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="Date To"
            value={dateTo}
            onChange={setDateTo}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button
            variant="contained"
            onClick={handleCreateReport}
            disabled={creatingReport || !name || !dateFrom || !dateTo}
          >
            {creatingReport ? <CircularProgress size={20} /> : "Create Report"}
          </Button>
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Reports
        </Typography>
        {reportsLoading && <CircularProgress size={20} />}
        {reports && reports.length === 0 && (
          <Typography color="textSecondary">No reports yet.</Typography>
        )}
        {(reports ?? []).map((report) => (
          <Paper
            key={report._id}
            sx={{ p: 1.5, mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", flexGrow: 1 }}
              onClick={() => navigate(`/benchmark/${report._id}`)}
            >
              <strong>{report.name}</strong>
              {" · "}
              {formatDate(report.daterange.from)} –{" "}
              {formatDate(report.daterange.to)}
              {" · "}
              Created by {report.createdBy.name} on{" "}
              {formatDate(report.createdAt)}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setConfirmId(report._id); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Paper>
        ))}
      </Box>
      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Delete Report"
        message="Are you sure you want to delete this report? All tables within it will also be deleted."
        onConfirm={() => { deleteReport(confirmId); setConfirmId(null); }}
        onCancel={() => setConfirmId(null)}
      />
    </Box>
  );
};

export default BenchmarkPage;